import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';

export const UserTable = ({
  users,
  currentUserId,
  selectedUsers,
  setSelectedUsers,
  userSortBy,
  setUserSortBy,
  userSortOrder,
  setUserSortOrder,
  userFilterBy,
  setUserFilterBy,
  userSearchTerm,
  setUserSearchTerm,
  onEditUser,
  onDeleteUser,
  onBulkDelete,
  onExport,
  onPrint,
  onResetPassword
}) => {
  // Sorting helper
  const sortData = (data, sortBy, sortOrder) => {
    return [...data].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === 'created_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  };

  // Get filtered and sorted users
  const getFilteredUsers = () => {
    let filtered = [...users];
    
    // Apply filter
    if (userFilterBy === 'admin') {
      filtered = filtered.filter(u => u.is_admin);
    } else if (userFilterBy === 'regular') {
      filtered = filtered.filter(u => !u.is_admin);
    }
    
    // Apply search
    if (userSearchTerm) {
      const term = userSearchTerm.toLowerCase();
      filtered = filtered.filter(u => 
        u.name.toLowerCase().includes(term) || 
        u.email.toLowerCase().includes(term)
      );
    }
    
    // Apply sort
    return sortData(filtered, userSortBy, userSortOrder);
  };

  const handleSelectAllUsers = (checked) => {
    if (checked) {
      setSelectedUsers(getFilteredUsers().map(u => u.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const filteredUsers = getFilteredUsers();

  return (
    <div className="space-y-4">
      <h2 className="text-3xl text-red-600 mb-4" 
          style={{ 
            fontFamily: 'Bebas Neue, sans-serif',
            letterSpacing: '4px',
            textShadow: 'none',
            fontWeight: '400'
          }}>
        MANAGE USERS
      </h2>
      
      {/* Toolbar */}
      <div className="bg-white p-4 rounded border-2 border-black flex flex-wrap gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Search */}
          <Input
            placeholder="Search name or email..."
            value={userSearchTerm}
            onChange={(e) => setUserSearchTerm(e.target.value)}
            className="w-48 border-2 border-black"
          />
          
          {/* Sort By */}
          <select
            value={userSortBy}
            onChange={(e) => setUserSortBy(e.target.value)}
            className="px-3 py-2 border-2 border-black rounded bg-white font-semibold"
          >
            <option value="created_at">Sort: Date</option>
            <option value="name">Sort: Name</option>
            <option value="email">Sort: Email</option>
          </select>
          
          {/* Sort Order */}
          <Button
            onClick={() => setUserSortOrder(userSortOrder === 'asc' ? 'desc' : 'asc')}
            variant="outline"
            className="border-2 border-black"
          >
            {userSortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
          </Button>
          
          {/* Filter By */}
          <select
            value={userFilterBy}
            onChange={(e) => setUserFilterBy(e.target.value)}
            className="px-3 py-2 border-2 border-black rounded bg-white font-semibold"
          >
            <option value="all">Filter: All Users</option>
            <option value="admin">Filter: Admins Only</option>
            <option value="regular">Filter: Regular Users</option>
          </select>
        </div>
        
        <div className="flex gap-3">
          {selectedUsers.length > 0 && (
            <Button
              onClick={onBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white border-2 border-black font-bold"
            >
              🗑️ Delete Selected ({selectedUsers.length})
            </Button>
          )}
          <Button
            onClick={onExport}
            className="bg-green-600 hover:bg-green-700 text-white border-2 border-black font-bold"
            data-testid="export-users-csv"
          >
            📊 Export CSV
          </Button>
          <Button
            onClick={onPrint}
            className="bg-blue-600 hover:bg-blue-700 text-white border-2 border-black font-bold"
            data-testid="print-users"
          >
            🖨️ Print
          </Button>
        </div>
      </div>
      
      {/* Spreadsheet Table */}
      <div className="bg-white rounded border-2 border-black overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead className="bg-gray-100 border-b-2 border-black">
            <tr>
              <th className="p-3 text-left w-12">
                <Checkbox
                  checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                  onCheckedChange={handleSelectAllUsers}
                />
              </th>
              <th className="p-3 text-left font-bold text-gray-800">Name</th>
              <th className="p-3 text-left font-bold text-gray-800">Email</th>
              <th className="p-3 text-left font-bold text-gray-800">Role</th>
              <th className="p-3 text-left font-bold text-gray-800">Alert Status</th>
              <th className="p-3 text-left font-bold text-gray-800">Created</th>
              <th className="p-3 text-left font-bold text-gray-800">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u, idx) => (
              <tr key={u.id} className={`border-b border-gray-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${u.red_flag ? 'bg-red-50' : ''} hover:bg-yellow-50`}>
                <td className="p-3">
                  <Checkbox
                    checked={selectedUsers.includes(u.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedUsers([...selectedUsers, u.id]);
                      } else {
                        setSelectedUsers(selectedUsers.filter(id => id !== u.id));
                      }
                    }}
                  />
                </td>
                <td className="p-3 font-semibold">
                  {u.name}
                  {u.red_flag && <span className="ml-2 text-red-500">🚩</span>}
                </td>
                <td className="p-3 text-gray-600">{u.email}</td>
                <td className="p-3">
                  {u.is_admin ? (
                    <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">ADMIN</span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs font-bold rounded">USER</span>
                  )}
                </td>
                <td className="p-3">
                  {u.alert_disabled ? (
                    <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">DISABLED</span>
                  ) : u.red_flag ? (
                    <span className="px-2 py-1 bg-yellow-500 text-white text-xs font-bold rounded">FLAGGED</span>
                  ) : (
                    <span className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded">ACTIVE</span>
                  )}
                </td>
                <td className="p-3 text-gray-600 text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => onResetPassword(u)}
                      size="sm"
                      className="bg-yellow-500 hover:bg-yellow-600 text-black text-xs font-bold"
                    >
                      Reset PW
                    </Button>
                    <Button
                      type="button"
                      onClick={() => onEditUser(u)}
                      size="sm"
                      className="bg-blue-500 hover:bg-blue-600 text-white text-xs"
                    >
                      Edit
                    </Button>
                    {u.id !== currentUserId && (
                      <Button
                        type="button"
                        onClick={() => onDeleteUser(u.id, u.name)}
                        size="sm"
                        variant="outline"
                        className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white text-xs"
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredUsers.length === 0 && (
          <div className="p-8 text-center text-gray-500">No users found matching your criteria</div>
        )}
      </div>
      <div className="text-sm text-gray-600">
        Showing {filteredUsers.length} of {users.length} users
      </div>
    </div>
  );
};
